import { apiClient } from './apiClient';
import type {
  ApiResponse,
  PaginatedResponse,
  DashboardStats,
  RevenueDashboard,
  AdminTherapist,
  CreateTherapistRequest,
  UpdateTherapistRequest,
  TherapistListQuery,
  AdminService,
  CreateServiceRequest,
  UpdateServiceRequest,
  ServiceListQuery,
  AdminBooking,
  BookingListQuery,
  CancelBookingRequest,
  AdminUser,
  AuditLogItem,
  AuditLogDetails,
  AuditLogsListQuery,
  ExportAuditLogsQuery,
  AnalyticsQuery,
  ExecutiveAnalytics,
  BookingAnalytics,
} from '../types/admin.types';

/**
 * Build query string from query object
 */
function buildQueryString(query: object): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

export const adminApi = {
  // ============ Auth / Verify ============

  /**
   * Verify admin access
   */
  async verifyAdmin(): Promise<{ user: AdminUser }> {
    const response = await apiClient.get<{ success: boolean; user: AdminUser }>(
      '/api/admin/verify'
    );
    return { user: response.data.user };
  },

  // ============ Dashboard ============

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get<ApiResponse<DashboardStats>>(
      '/api/admin/dashboard/stats'
    );
    return response.data.data;
  },

  /**
   * Get revenue dashboard data
   */
  async getRevenueDashboard(startDate?: string, endDate?: string): Promise<RevenueDashboard> {
    const query = buildQueryString({ startDate, endDate });
    const response = await apiClient.get<ApiResponse<RevenueDashboard>>(
      `/api/admin/dashboard/revenue${query}`
    );
    return response.data.data;
  },

  // ============ Therapists ============

  /**
   * List all therapists with pagination and filtering
   */
  async listTherapists(
    query: TherapistListQuery = {}
  ): Promise<PaginatedResponse<AdminTherapist[]>> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<PaginatedResponse<AdminTherapist[]>>(
      `/api/admin/therapists${queryString}`
    );
    return response.data;
  },

  /**
   * Get a single therapist by ID
   */
  async getTherapist(id: string): Promise<AdminTherapist> {
    const response = await apiClient.get<ApiResponse<AdminTherapist>>(
      `/api/admin/therapists/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new therapist
   */
  async createTherapist(data: CreateTherapistRequest): Promise<AdminTherapist> {
    const response = await apiClient.post<ApiResponse<AdminTherapist>>(
      '/api/admin/therapists',
      data
    );
    return response.data.data;
  },

  /**
   * Update a therapist
   */
  async updateTherapist(id: string, data: UpdateTherapistRequest): Promise<AdminTherapist> {
    const response = await apiClient.put<ApiResponse<AdminTherapist>>(
      `/api/admin/therapists/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete (deactivate) a therapist
   */
  async deleteTherapist(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/therapists/${id}`);
  },

  // ============ Services ============

  /**
   * List all services with pagination and filtering
   */
  async listServices(query: ServiceListQuery = {}): Promise<PaginatedResponse<AdminService[]>> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<PaginatedResponse<AdminService[]>>(
      `/api/admin/services${queryString}`
    );
    return response.data;
  },

  /**
   * Get a single service by ID
   */
  async getService(id: string): Promise<AdminService> {
    const response = await apiClient.get<ApiResponse<AdminService>>(
      `/api/admin/services/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new service
   */
  async createService(data: CreateServiceRequest): Promise<AdminService> {
    const response = await apiClient.post<ApiResponse<AdminService>>(
      '/api/admin/services',
      data
    );
    return response.data.data;
  },

  /**
   * Update a service
   */
  async updateService(id: string, data: UpdateServiceRequest): Promise<AdminService> {
    const response = await apiClient.put<ApiResponse<AdminService>>(
      `/api/admin/services/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete (deactivate) a service
   */
  async deleteService(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/services/${id}`);
  },

  // ============ Bookings ============

  /**
   * List all bookings with pagination and filtering
   */
  async listBookings(query: BookingListQuery = {}): Promise<PaginatedResponse<AdminBooking[]>> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<PaginatedResponse<AdminBooking[]>>(
      `/api/admin/bookings${queryString}`
    );
    return response.data;
  },

  /**
   * Get a single booking by ID
   */
  async getBooking(id: string): Promise<AdminBooking> {
    const response = await apiClient.get<ApiResponse<AdminBooking>>(
      `/api/admin/bookings/${id}`
    );
    return response.data.data;
  },

  /**
   * Cancel a booking
   */
  async cancelBooking(id: string, data: CancelBookingRequest = {}): Promise<void> {
    await apiClient.post(`/api/admin/bookings/${id}/cancel`, data);
  },

  // ============ Audit Logs ============

  /**
   * List audit logs with pagination and filtering
   */
  async listAuditLogs(
    query: AuditLogsListQuery = {}
  ): Promise<PaginatedResponse<AuditLogItem[]>> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<PaginatedResponse<AuditLogItem[]>>(
      `/api/admin/audit-logs${queryString}`
    );
    return response.data;
  },

  /**
   * Get a single audit log by ID with full details
   */
  async getAuditLog(id: string): Promise<AuditLogDetails> {
    const response = await apiClient.get<ApiResponse<AuditLogDetails>>(
      `/api/admin/audit-logs/${id}`
    );
    return response.data.data;
  },

  /**
   * Export audit logs as CSV or JSON
   */
  async exportAuditLogs(query: ExportAuditLogsQuery): Promise<Blob> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get(`/api/admin/audit-logs/export${queryString}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // ============ Analytics ============

  /**
   * Get executive analytics (high-level KPIs)
   */
  async getExecutiveAnalytics(query: AnalyticsQuery = {}): Promise<ExecutiveAnalytics> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<ApiResponse<ExecutiveAnalytics>>(
      `/api/admin/analytics/executive${queryString}`
    );
    return response.data.data;
  },

  /**
   * Get booking analytics (detailed patterns and trends)
   */
  async getBookingAnalytics(query: AnalyticsQuery = {}): Promise<BookingAnalytics> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<ApiResponse<BookingAnalytics>>(
      `/api/admin/analytics/bookings${queryString}`
    );
    return response.data.data;
  },
};
